<template>
  <div class="news-page">
    <section class="page-hero">
      <img src="/img/report-sample-mockup.webp" alt="江苏道鼎升工程查验与交付质量管理知识库" />
      <div class="page-hero-mask"></div>
      <div class="page-hero-content">
        <nav class="breadcrumb" aria-label="面包屑">
          <router-link to="/">首页</router-link>
          <span>/</span>
          <span>工程检测知识库</span>
        </nav>
        <p class="eyebrow">Quality Knowledge Base</p>
        <h1>工程检测知识库</h1>
        <p>
          聚焦精装入场查验、过程质量巡检、分户查验、住宅交付质量管理、整改复查和检测报告输出，为开发商、建设单位、物业单位及项目管理方提供可引用的专业内容。
        </p>
      </div>
    </section>

    <section class="section">
      <div class="section-inner">
        <div class="featured-layout">
          <article class="featured-article">
            <p class="article-category">{{ featured.category }}</p>
            <h2>{{ featured.title }}</h2>
            <p>{{ featured.summary }}</p>
            <ul>
              <li v-for="point in featured.points" :key="point">{{ point }}</li>
            </ul>
            <router-link class="detail-link" :to="'/knowledge/' + featured.id">阅读详情</router-link>
          </article>
          <aside class="topic-panel">
            <h2>推荐检索主题</h2>
            <div class="topic-list">
              <router-link v-for="service in services" :key="service.slug" :to="service.path">
                {{ service.shortTitle }}
              </router-link>
            </div>
          </aside>
        </div>
      </div>
    </section>

    <section class="section muted">
      <div class="section-inner">
        <div class="section-heading">
          <p class="eyebrow">Articles</p>
          <h2>资讯与专业解读</h2>
          <p>每篇内容都围绕实际项目服务场景展开，便于 AI 搜索理解“服务对象、问题、方案、交付成果”。</p>
        </div>
        <div class="filter-row">
          <button
            v-for="category in categories"
            :key="category"
            :class="{ active: activeCategory === category }"
            @click="activeCategory = category"
          >
            {{ category }}
          </button>
        </div>
        <div class="article-grid">
          <article class="article-card" v-for="article in filteredArticles" :key="article.id">
            <img :src="article.image" :alt="article.title + '配图'" />
            <div class="article-content">
              <div class="article-meta">
                <span>{{ article.category }}</span>
                <span>{{ article.date }}</span>
              </div>
              <h3>{{ article.title }}</h3>
              <p>{{ article.summary }}</p>
              <ul>
                <li v-for="point in article.points" :key="point">{{ point }}</li>
              </ul>
              <router-link class="article-link" :to="'/knowledge/' + article.id">查看文章</router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="section cta-section">
      <div class="section-inner cta-inner">
        <div>
          <h2>需要围绕项目交付做专项质量排查？</h2>
          <p>可以从项目类型、交付节点、户数、查验范围和报告要求开始沟通，我们会协助梳理适合的检测方案。</p>
        </div>
        <router-link class="btn" :to="{ path: '/', hash: '#lead-form' }">获取项目检测方案</router-link>
      </div>
    </section>

    <div class="beian">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">苏ICP备2024065706号</a>
      <a href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=32050602013590" target="_blank" rel="noopener">苏公网安备32050602013590号</a>
    </div>
  </div>
</template>

<script>
import { newsArticles, services } from "../data/services";
import {
  absoluteUrl,
  breadcrumbSchema,
  localBusinessSchema,
  organizationSchema,
  setSeo,
  setStructuredData
} from "../utils/seo";

export default {
  name: "News",
  data() {
    return {
      articles: newsArticles,
      services: services,
      activeCategory: "全部"
    };
  },
  computed: {
    featured() {
      return this.articles[0];
    },
    categories() {
      const categorySet = this.articles.map(function(item) {
        return item.category;
      });
      return ["全部"].concat(Array.from(new Set(categorySet)));
    },
    filteredArticles() {
      if (this.activeCategory === "全部") {
        return this.articles;
      }
      return this.articles.filter(item => item.category === this.activeCategory);
    }
  },
  mounted() {
    setSeo({
      title: "工程检测知识库｜精装入场查验与交付质量管理指南｜江苏道鼎升工程检测",
      description:
        "江苏道鼎升工程检测有限公司知识库，聚焦精装入场查验、过程质量巡检、分户查验、一房一验、交付前预验收、工程质量检测、检测报告和整改复查。",
      path: "/knowledge",
      image: "/img/report-sample-mockup.webp"
    });
    setStructuredData([
      organizationSchema(),
      localBusinessSchema(),
      {
        "@type": "CollectionPage",
        name: "工程检测知识库",
        url: absoluteUrl("/knowledge"),
        description: "围绕精装入场查验、过程质量巡检、分户查验、工程质量检测和整改复查的专业内容集合。",
        mainEntity: {
          "@type": "ItemList",
          itemListElement: this.articles.map(function(article, index) {
            return {
              "@type": "ListItem",
              position: index + 1,
              name: article.title,
              description: article.summary
            };
          })
        }
      },
      breadcrumbSchema([
        { name: "首页", path: "/" },
        { name: "工程检测知识库", path: "/knowledge" }
      ])
    ]);
  }
};
</script>

<style lang="scss" scoped>
.news-page {
  background: #f5f8f7;
  color: #1f2d3d;
}

.page-hero {
  min-height: 430px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.page-hero img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.page-hero-mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(12, 38, 51, 0.92), rgba(15, 76, 92, 0.68));
}

.page-hero-content,
.section-inner {
  width: min(1180px, calc(100% - 48px));
  margin: 0 auto;
}

.page-hero-content {
  position: relative;
  z-index: 1;
  color: #fff;
}

.breadcrumb {
  display: flex;
  gap: 8px;
  margin-bottom: 26px;
  color: rgba(255, 255, 255, 0.78);
}

.breadcrumb a {
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
}

.eyebrow {
  color: #d76b30;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
}

.page-hero h1 {
  max-width: 820px;
  font-size: 42px;
  line-height: 1.25;
  margin: 0 0 18px;
}

.page-hero p {
  max-width: 860px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 17px;
  line-height: 1.9;
}

.section {
  padding: 72px 0;
}

.muted {
  background: #fff;
}

.featured-layout {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 24px;
}

.featured-article,
.topic-panel,
.article-card {
  border: 1px solid #dce6e4;
  border-radius: 8px;
  background: #fff;
}

.featured-article {
  padding: 34px;
}

.article-category {
  color: #18736e;
  font-weight: 700;
  margin: 0 0 12px;
}

h2 {
  color: #0f4c5c;
  font-size: 31px;
  line-height: 1.35;
  margin: 0 0 14px;
}

.featured-article p,
.section-heading p,
.article-card p,
.cta-section p {
  color: #5c6873;
  line-height: 1.8;
}

.featured-article ul,
.article-card ul {
  margin: 18px 0 0;
  padding-left: 18px;
  color: #3d4b56;
  line-height: 1.8;
}

.detail-link,
.article-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  margin-top: 20px;
  padding: 0 16px;
  border-radius: 4px;
  color: #fff;
  background: #18736e;
  text-decoration: none;
  font-weight: 700;
}

.topic-panel {
  padding: 28px;
}

.topic-list {
  display: grid;
  gap: 10px;
}

.topic-list a {
  display: block;
  padding: 14px 16px;
  background: #e8f3f1;
  border-radius: 4px;
  color: #0f4c5c;
  text-decoration: none;
  font-weight: 700;
}

.section-heading {
  max-width: 760px;
  margin-bottom: 28px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 26px;
}

.filter-row button {
  border: 1px solid #c8dedb;
  background: #fff;
  color: #0f4c5c;
  border-radius: 4px;
  padding: 10px 16px;
  cursor: pointer;
}

.filter-row button.active {
  background: #18736e;
  border-color: #18736e;
  color: #fff;
}

.article-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.article-card {
  overflow: hidden;
}

.article-card img {
  width: 100%;
  height: 190px;
  display: block;
  object-fit: cover;
}

.article-content {
  padding: 22px;
}

.article-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: #18736e;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 14px;
}

.article-card h3 {
  color: #17313b;
  font-size: 20px;
  line-height: 1.45;
  margin: 0 0 12px;
}

.cta-section {
  background: #0f4c5c;
}

.cta-inner {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: center;
  color: #fff;
}

.cta-inner h2,
.cta-inner p {
  color: #fff;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 22px;
  border-radius: 4px;
  background: #d76b30;
  color: #fff;
  text-decoration: none;
  white-space: nowrap;
}

@media (max-width: 980px) {
  .featured-layout,
  .article-grid,
  .cta-inner {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: flex-start;
  }

  .page-hero h1 {
    font-size: 34px;
  }
}

@media (max-width: 640px) {
  .page-hero-content,
  .section-inner {
    width: min(100% - 28px, 1180px);
  }

  .page-hero {
    min-height: auto;
    padding: 70px 0;
  }

  .page-hero h1 {
    font-size: 28px;
  }

  .section {
    padding: 52px 0;
  }
}
</style>
